import { useState, useEffect, useRef, useCallback } from "react";
import { useStudyTimeStore } from "../../../hooks/useStudyTimeStore";

interface Props {
  classId: number;
  classTitle: string;
}

const FOCUS_SEC = 25 * 60;
const BREAK_SEC = 5 * 60;

export default function FocusTimer({ classId, classTitle }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"focus" | "break">("focus");
  const [secondsLeft, setSecondsLeft] = useState(FOCUS_SEC);
  const [running, setRunning] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const [sessionStartMs, setSessionStartMs] = useState<number | null>(null);
  const [justFinished, setJustFinished] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { addSession, totalMs } = useStudyTimeStore();

  const totalMin = Math.floor(totalMs / 60000);
  const totalHour = Math.floor(totalMin / 60);
  const remMin = totalMin % 60;
  const totalLabel = totalHour > 0 ? `${totalHour}h ${remMin}m` : `${totalMin}m`;

  const tick = useCallback(() => {
    setSecondsLeft((prev) => {
      if (prev <= 1) {
        setRunning(false);
        setJustFinished(true);
        if (mode === "focus" && sessionStartMs !== null) {
          addSession(classId, classTitle, Date.now() - sessionStartMs);
          setSessionCount((c) => c + 1);
        }
        setTimeout(() => setJustFinished(false), 3000);
        return mode === "focus" ? BREAK_SEC : FOCUS_SEC;
      }
      return prev - 1;
    });
  }, [mode, sessionStartMs, addSession, classId, classTitle]);

  useEffect(() => {
    if (running) {
      if (sessionStartMs === null) setSessionStartMs(Date.now());
      intervalRef.current = setInterval(tick, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [running, tick, sessionStartMs]);

  const handleStart = () => {
    if (!running) setSessionStartMs(Date.now());
    setRunning(true);
  };

  const handlePause = () => {
    if (running && sessionStartMs !== null) {
      addSession(classId, classTitle, Date.now() - sessionStartMs);
      setSessionStartMs(null);
    }
    setRunning(false);
  };

  const handleReset = () => {
    setRunning(false);
    setSessionStartMs(null);
    setSecondsLeft(mode === "focus" ? FOCUS_SEC : BREAK_SEC);
  };

  const toggleMode = () => {
    setRunning(false);
    setSessionStartMs(null);
    const next = mode === "focus" ? "break" : "focus";
    setMode(next);
    setSecondsLeft(next === "focus" ? FOCUS_SEC : BREAK_SEC);
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const progress = mode === "focus" ? 1 - secondsLeft / FOCUS_SEC : 1 - secondsLeft / BREAK_SEC;
  const R = 28;
  const circ = 2 * Math.PI * R;
  const timerColor = mode === "focus" ? "#00D873" : "#FF8C00";

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen(!open)}
        title="집중 타이머"
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-all cursor-pointer ${
          open ? "bg-[#191F28]" : "bg-white border-2 border-[#F2F4F6] hover:border-[#00D873]"
        }`}
        style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}
      >
        {running ? (
          <div className="relative w-7 h-7 flex items-center justify-center">
            <svg className="absolute -rotate-90 w-7 h-7" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="28" fill="none" stroke={open ? "rgba(255,255,255,0.1)" : "#F2F4F6"} strokeWidth="5" />
              <circle cx="32" cy="32" r="28" fill="none" stroke={timerColor} strokeWidth="5"
                strokeDasharray={`${progress * circ * 2.28} ${circ * 2.28}`} strokeLinecap="round" />
            </svg>
            <i className={`ri-pause-line text-sm ${open ? "text-white" : "text-[#191F28]"}`} />
          </div>
        ) : (
          <i className={`ri-timer-line text-xl ${open ? "text-white" : "text-[#8B95A1]"}`} />
        )}
      </button>

      {/* Timer Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 bg-white rounded-3xl p-6 w-64"
          style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}
        >
          {/* Mode toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex bg-[#F2F4F6] rounded-full p-1 gap-0.5">
              {(["focus", "break"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setRunning(false); setSessionStartMs(null); setSecondsLeft(m === "focus" ? FOCUS_SEC : BREAK_SEC); }}
                  className={`text-[11px] font-bold px-3 py-1 rounded-full transition-all cursor-pointer whitespace-nowrap ${mode === m ? "bg-white text-[#191F28]" : "text-[#8B95A1]"}`}
                >
                  {m === "focus" ? "🎯 집중" : "☕ 휴식"}
                </button>
              ))}
            </div>
            {sessionCount > 0 && (
              <span className="text-[10px] font-bold text-[#FF8C00]">{sessionCount}회 완료</span>
            )}
          </div>

          {/* Timer ring */}
          <div className="flex items-center justify-center mb-4 relative">
            <svg className="-rotate-90" width="120" height="120" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r={R} fill="none" stroke="#F2F4F6" strokeWidth="4.5" />
              <circle cx="32" cy="32" r={R} fill="none" stroke={timerColor} strokeWidth="4.5"
                strokeDasharray={`${progress * circ} ${circ}`} strokeLinecap="round"
                style={{ transition: "stroke-dasharray 0.8s ease" }} />
            </svg>
            <div className="absolute flex flex-col items-center">
              <span className="text-2xl font-black text-[#191F28] tracking-tight">{mm}:{ss}</span>
              <span className="text-[10px] text-[#8B95A1] font-medium">{mode === "focus" ? "집중 시간" : "휴식 시간"}</span>
            </div>
          </div>

          {/* Celebration */}
          {justFinished && (
            <div className="mb-3 bg-[#E8F5E9] rounded-xl p-3 text-center">
              <p className="text-xs font-extrabold text-[#00D873]">
                {mode === "break" ? "🔥 집중 세션 완료!" : "☕ 휴식 시간이에요!"}
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="flex gap-2">
            {!running ? (
              <button
                onClick={handleStart}
                className="flex-1 py-2.5 rounded-xl text-sm font-extrabold text-white cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: timerColor }}
              >시작</button>
            ) : (
              <button
                onClick={handlePause}
                className="flex-1 py-2.5 rounded-xl text-sm font-extrabold text-white cursor-pointer bg-[#191F28] whitespace-nowrap"
              >일시정지</button>
            )}
            <button
              onClick={handleReset}
              className="w-10 h-10 rounded-xl bg-[#F2F4F6] flex items-center justify-center cursor-pointer hover:bg-[#E8E9EB] flex-shrink-0"
            >
              <i className="ri-refresh-line text-[#8B95A1]" />
            </button>
          </div>

          {/* Total time */}
          {totalMin > 0 && (
            <div className="mt-3 pt-3 border-t border-[#F2F4F6] flex items-center justify-between">
              <span className="text-[11px] text-[#8B95A1]">총 학습 시간</span>
              <span className="text-[11px] font-extrabold text-[#191F28]">{totalLabel}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
